import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import RangeEdit from '../../components/RangeEdit/RangeEdit';

import selector from './holesEditor.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import {
  selectFirstHeight,
  selectSecondHeight,
  selectLength,
  selectWidth,
  setSelected as setSelectedParameter,
  setWindows
} from '../Construction/construction.reducer';
import withTranslation from '../../components/WithTranslation/WithTranslation';

const Container = styled.div`
  background: ${colors.whiteer};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 300px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

// --- helpers ---
const parseSize = (opt) => {
  if (!opt) return { width: 0, height: 0 };

  if (typeof opt.width === 'number' && typeof opt.height === 'number') {
    return { width: opt.width, height: opt.height };
  }

  // 2) Spróbuj z value (string) np. "80x60", "80 x 60", "80cm x 60cm"
  const val = String(opt.value ?? '').toLowerCase();
  // najpierw "NxM" z x/×
  let m = val.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (m) {
    return { width: parseInt(m[1], 10) || 0, height: parseInt(m[2], 10) || 0 };
  }
  // fallback: weź pierwsze dwie liczby ze stringa
  const nums = (val.match(/\d+/g) || []).map((n) => parseInt(n, 10));
  if (nums.length >= 2) return { width: nums[0] || 0, height: nums[1] || 0 };

  return { width: 0, height: 0 };
};

const getSelectedSize = (available, selectedId) => {
  const opt = available?.find((el) => el.id === selectedId);
  if (!opt) return { width: 0, height: 0 };

  const str = String(opt.value || '').toLowerCase();
  const m = str.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (!m) return { width: 0, height: 0 };

  return {
    width: Number(m[1]),
    height: Number(m[2]),
  };
};

const ConstructionExtras = ({
                              collapsed,
                              loaded = false,

                              hole_back_count_available = [],
                              hole_back_count_info = null,
                              hole_back_count_selected = null,
                              hole_back_size_available = [],
                              hole_back_size_info = null,
                              hole_back_size_selected = null,

                              // hole_front_count_available,
                              // hole_front_count_info,
                              // hole_front_count_selected,
                              // hole_front_size_available,
                              // hole_front_size_info,
                              // hole_front_size_selected,

                              hole_left_count_available = [],
                              hole_left_count_info = null,
                              hole_left_count_selected = null,
                              hole_left_size_available = [],
                              hole_left_size_info = null,
                              hole_left_size_selected = null,

                              hole_right_count_available = [],
                              hole_right_count_info = null,
                              hole_right_count_selected = null,
                              hole_right_size_available = [],
                              hole_right_size_info = null,
                              hole_right_size_selected = null,

                              hole_front_count_available = [],
                              hole_front_count_info = null,
                              hole_front_count_selected = null,
                              hole_front_size_available = [],
                              hole_front_size_info = null,
                              hole_front_size_selected = null,

                              setSelected,
                              t
                            }) => {
  // Modal – jak w oryginalnym holes
  const openModal = () => {
    Modal.info({
      title: hole_left_size_info && hole_left_size_info.info_title,
      content: (
        <React.Fragment>
          {hole_left_size_info ? (
            <React.Fragment>
              {hole_left_size_info.img_one ? <ImgWrapper src={hole_left_size_info.img_one} /> : null}
              {hole_left_size_info.img_two ? <ImgWrapper src={hole_left_size_info.img_two} /> : null}
              {hole_left_size_info.info_content}
              {hole_left_size_info.img_three ? <ImgWrapper src={hole_left_size_info.img_three} /> : null}
              {hole_left_size_info.img_four ? <ImgWrapper src={hole_left_size_info.img_four} /> : null}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      )
    });
  };

  // Wymiary hali
  const width = useSelector(selectWidth);
  const length = useSelector(selectLength);
  const firstHeight = useSelector(selectFirstHeight);
  const secondHeight = useSelector(selectSecondHeight);

  // Redux
  const dispatch = useDispatch();
  const windows = useSelector((state) => state.Construction.windows);

  // Zmiana pozycji jednego otworu (typ = 'openings')
  const onHolePositionChange = (wall, index, key, value) => {
    const updated = (windows[wall]?.openings || []).map((d, i) =>
      i === index ? { ...d, [key]: value } : d
    );
    dispatch(setWindows(wall, 'openings', updated));
  };

  // Liczba otworów z "available -> value/id"
  const getCountFromAvailable = (available, selectedId) => {
    const opt = available?.find((el) => el.id === selectedId);
    if (!opt) return 0;
    const n = parseInt(String(opt.value).replace(/[^\d-]/g, ''), 10);
    if (!Number.isNaN(n)) return n;
    const v = String(opt.value || '').toLowerCase();
    if (v.includes('brak') || v.includes('0')) return 0;
    if (v.includes('1')) return 1;
    if (v.includes('2')) return 2;
    return 0;
  };

  // Zmiana LICZBY otworów: tworzymy nowe wpisy z aktualnie wybranym rozmiarem dla danej ściany
  const handleHoleCountChange = (wall, paramKey, selectedId, countAvailable, sizeAvailable, sizeSelectedId) => {
    const holesCount = getCountFromAvailable(countAvailable, selectedId);
    const { width: holeW, height: holeH } = getSelectedSize(sizeAvailable, sizeSelectedId);

    const current = windows[wall]?.openings || [];
    const updated = Array.from({ length: holesCount }, (_, index) => {
      const existing = current[index];
      // Jeśli istniejący wpis ma szer/wys, zostawiamy; jeśli nie, nadajemy z wybranego rozmiaru
      if (existing) {
        return {
          ...existing,
          width: typeof existing.width === 'number' && existing.width > 0 ? existing.width : holeW,
          height: typeof existing.height === 'number' && existing.height > 0 ? existing.height : holeH
        };
      }
      return {
        id: index,
        positionHorizontal: 20,
        positionVertical: 50,
        width: holeW,
        height: holeH
      };
    });

    dispatch(setWindows(wall, 'openings', updated));
    setSelected(paramKey, selectedId);
  };

  // Zmiana ROZMIARU: aktualizujemy width/height wszystkich otworów na ścianie
  const handleHoleSizeChange = (wall, paramKey, selectedId, sizeAvailable) => {
    const { width: holeW, height: holeH } = getSelectedSize(sizeAvailable, selectedId);
    const current = windows[wall]?.openings || [];
    const updated = current.map((d) => ({
      ...d,
      width: holeW,
      height: holeH
    }));

    dispatch(setWindows(wall, 'openings', updated));
    setSelected(paramKey, selectedId);
  };

  // pomocniczo, aby nie powtarzać w JSX
  const renderWallControls = (wallKey, wallLabelForCount, wallLabelForSize, countAvailable, countSelected, countInfo,
                              sizeAvailable, sizeSelected, sizeInfo, maxHorizontalBy) => (
    <>
      <SelectEdit
        selected={countSelected}
        loaded={loaded}
        title={countInfo?.name}
        onChange={(id) =>
          handleHoleCountChange(
            wallKey,
            wallLabelForCount,
            id,
            countAvailable,
            sizeAvailable,
            sizeSelected
          )
        }
        options={countAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      <SelectEdit
        selected={sizeSelected}
        loaded={loaded}
        title={sizeInfo?.name}
        onChange={(id) => handleHoleSizeChange(wallKey, wallLabelForSize, id, sizeAvailable)}
        options={sizeAvailable.map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      {(windows[wallKey]?.openings || []).map((hole, index, arr) => (
        <React.Fragment key={`${hole.id}_group`}>
          <RangeEdit
            key={`${hole.id}_horizontal`}
            title={
              arr.length === 1
                ? t('hole_position_horizontal')
                : t('hole_position_horizontal') + ' 2'
            }
            min={20}
            step={5}
            max={maxHorizontalBy - 20 - hole.width}
            unit={'cm'}
            selected={hole.positionHorizontal ?? 20}
            onChange={(value) => onHolePositionChange(wallKey, index, 'positionHorizontal', value)}
          />
          <RangeEdit
            key={`${hole.id}_vertical`}
            title={
              arr.length === 1
                ? t('hole_position_vertical')
                : t('hole_position_vertical') + ' 2'
            }
            min={50}
            step={5}
            max={secondHeight - 13 - hole.height}
            unit={'cm'}
            selected={hole.positionVertical ?? 50}
            onChange={(value) => onHolePositionChange(wallKey, index, 'positionVertical', value)}
          />
        </React.Fragment>
      ))}
    </>
  );

  return (
    <React.Fragment>
      {loaded ? (
        <EditableSection
          collapsed={collapsed}
          title={t('ordinary_window_openings')}
          nested
          subsection={true}
          open={false}
        >
          <Spacer>{t('please_describe_location_of_holes')}</Spacer>

          {/* BACK WALL (max poziomy wg width) */}
          {renderWallControls(
            'back_wall',
            'hole_back_count',
            'hole_back_size',
            hole_back_count_available,
            hole_back_count_selected,
            hole_back_count_info,
            hole_back_size_available,
            hole_back_size_selected,
            hole_back_size_info,
            width
          )}

          {/* LEFT WALL (max poziomy wg length) */}
          {renderWallControls(
            'left_wall',
            'hole_left_count',
            'hole_left_size',
            hole_left_count_available,
            hole_left_count_selected,
            hole_left_count_info,
            hole_left_size_available,
            hole_left_size_selected,
            hole_left_size_info,
            length
          )}

          {/* RIGHT WALL (max poziomy wg length) */}
          {renderWallControls(
            'right_wall',
            'hole_right_count',
            'hole_right_size',
            hole_right_count_available,
            hole_right_count_selected,
            hole_right_count_info,
            hole_right_size_available,
            hole_right_size_selected,
            hole_right_size_info,
            length
          )}

          {/* front WALL (max poziomy wg length) */}
          {renderWallControls(
            'front_wall',
            'hole_front_count',
            'hole_front_size',
            hole_front_count_available,
            hole_front_count_selected,
            hole_front_count_info,
            hole_front_size_available,
            hole_front_size_selected,
            hole_front_size_info,
            width
          )}

          <Container>
            <ButtonWrapper>
              <Button icon={<InfoCircleOutlined />} type="primary" block onClick={openModal}>
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      ) : null}
    </React.Fragment>
  );
};

ConstructionExtras.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
  loaded: PropTypes.bool,

  hole_back_count_available: PropTypes.arrayOf(availableType),
  hole_back_count_info: infoShape,
  hole_back_count_selected: PropTypes.number,
  hole_back_size_available: PropTypes.arrayOf(availableType),
  hole_back_size_info: infoShape,
  hole_back_size_selected: PropTypes.number,

  // hole_front_count_available: PropTypes.arrayOf(availableType),
  // hole_front_count_info: infoShape,
  // hole_front_count_selected: PropTypes.number,
  // hole_front_size_available: PropTypes.arrayOf(availableType),
  // hole_front_size_info: infoShape,
  // hole_front_size_selected: PropTypes.number,

  hole_left_count_available: PropTypes.arrayOf(availableType),
  hole_left_count_info: infoShape,
  hole_left_count_selected: PropTypes.number,
  hole_left_size_available: PropTypes.arrayOf(availableType),
  hole_left_size_info: infoShape,
  hole_left_size_selected: PropTypes.number,

  hole_right_count_available: PropTypes.arrayOf(availableType),
  hole_right_count_info: infoShape,
  hole_right_count_selected: PropTypes.number,
  hole_right_size_available: PropTypes.arrayOf(availableType),
  hole_right_size_info: infoShape,
  hole_right_size_selected: PropTypes.number,

  hole_front_count_available: PropTypes.arrayOf(availableType),
  hole_front_count_info: infoShape,
  hole_front_count_selected: PropTypes.number,
  hole_front_size_available: PropTypes.arrayOf(availableType),
  hole_front_size_info: infoShape,
  hole_front_size_selected: PropTypes.number
};

const mapDispatchToProps = {
  setSelected: setSelectedParameter
};

export default connect(selector, mapDispatchToProps)(withTranslation(ConstructionExtras));
